/**
 * Apres un import, le fichier d'export de l'autre gestionnaire reste sur le disque
 * de l'utilisateur. S'il etait EN CLAIR, Klavox le DIT et PROPOSE de le supprimer
 * (contrat `bus/klavox-credentials.v1.md` § 3) — jamais d'effacement automatique :
 * c'est le fichier de l'utilisateur.
 *
 * Un export qui a demande un mot de passe pour s'ouvrir (KDBX, Bitwarden protege,
 * CXF chiffre Klavox) n'expose rien : pas de proposition. Un KDBX est toujours
 * chiffre, meme quand il s'ouvre par un fichier cle seul.
 */
export function isPlaintextExport(fileName: string, password: string | undefined): boolean {
  if (password !== undefined) return false;
  return !/\.kdbx$/iu.test(fileName.trim());
}

/** Consigne precise, la ou la plateforme ne permet pas de supprimer en un clic. */
export const IMPORT_CLEANUP_ADVICE =
  'Supprimez-le maintenant, puis videz la corbeille. S’il est synchronise (iCloud Drive, OneDrive, Google Drive, Dropbox), supprimez-le aussi de ce service et de sa corbeille. Klavox ne le supprime jamais a votre place.';

/**
 * Poignee du fichier choisi, la ou le navigateur la donne (File System Access : selecteur natif ou depot, Chrome et
 * Edge). C'est la seule voie d'une suppression EN UN CLIC ; partout ailleurs, la consigne ci-dessus.
 * Nom et poignee seulement : jamais le contenu, qui ne vit qu'en memoire le temps de l'import.
 */
export interface ImportFileHandle {
  readonly name: string;
  getFile(): Promise<File>;
  remove?: () => Promise<void>;
  requestPermission?: (descriptor: { mode: 'readwrite' }) => Promise<PermissionState>;
}

/** Consigne la ou le navigateur sait supprimer le fichier : sur demande seulement, et la copie synchronisee reste. */
export const IMPORT_CLEANUP_ONE_CLICK_ADVICE =
  'Klavox peut le supprimer de cet appareil sur votre demande : le navigateur vous demandera d’abord l’autorisation. S’il est synchronise (iCloud Drive, OneDrive, Google Drive, Dropbox), supprimez-le aussi de ce service et de sa corbeille.';

/** Consigne apres une suppression faite par Klavox : la copie locale est partie, pas celle d'un service de synchro. */
export const IMPORT_REMOVED_ADVICE =
  'S’il etait synchronise (iCloud Drive, OneDrive, Google Drive, Dropbox), supprimez-le aussi de ce service et de sa corbeille.';

/** La plateforme sait-elle supprimer CE fichier sur demande ? Sinon, pas de bouton : une promesse tenue ou rien. */
export function canRemoveImportFile(handle: ImportFileHandle | null): handle is ImportFileHandle & { remove: () => Promise<void> } {
  return handle !== null && typeof handle.remove === 'function';
}

/** `supprime` ; `deja-absent` (deplace ou supprime entre-temps) ; `refuse` (droit d'ecriture refuse) ; `impossible`. */
export type ImportFileRemoval = 'supprime' | 'deja-absent' | 'refuse' | 'impossible';

/**
 * Supprime le fichier d'export choisi, au clic de l'utilisateur et seulement la : le droit d'ecriture est demande
 * d'abord (le navigateur le montre), et rien n'est tente sans lui.
 */
export async function removeImportFile(handle: ImportFileHandle | null): Promise<ImportFileRemoval> {
  if (!canRemoveImportFile(handle)) return 'impossible';
  if (typeof handle.requestPermission === 'function') {
    const permission = await handle.requestPermission({ mode: 'readwrite' }).catch((): PermissionState => 'denied');
    if (permission !== 'granted') return 'refuse';
  }
  try {
    await handle.remove();
    return 'supprime';
  } catch (cause) {
    const name = cause instanceof Error || cause instanceof DOMException ? cause.name : '';
    if (name === 'NotFoundError') return 'deja-absent';
    if (name === 'NotAllowedError' || name === 'SecurityError') return 'refuse';
    return 'impossible';
  }
}

interface FilePickerWindow {
  showOpenFilePicker?: (options: {
    multiple: false;
    excludeAcceptAllOption: false;
    types: { description: string; accept: Record<string, string[]> }[];
  }) => Promise<ImportFileHandle[]>;
}

/** Le navigateur a-t-il le selecteur natif qui rend une poignee ? Lu au clic, avant d'ecarter le champ fichier. */
export function hasImportFilePicker(win: unknown): boolean {
  return typeof (win as FilePickerWindow).showOpenFilePicker === 'function';
}

/**
 * Selecteur natif du navigateur, qui rend une poignee supprimable. `undefined` : la plateforme n'en a pas (Firefox,
 * Safari) -- l'appelant garde le champ fichier ordinaire ; `null` : l'utilisateur a annule.
 * `accept` = l'attribut du champ fichier : seules ses extensions sont reprises.
 */
export async function pickImportFile(win: unknown, accept: string): Promise<{ file: File; handle: ImportFileHandle } | null | undefined> {
  const picker = (win as FilePickerWindow).showOpenFilePicker;
  if (typeof picker !== 'function') return undefined;
  const extensions = accept.split(',').map((part) => part.trim()).filter((part) => /^\.[a-z0-9]+$/iu.test(part));
  let handles: ImportFileHandle[];
  try {
    handles = await picker.call(win, {
      multiple: false,
      excludeAcceptAllOption: false,
      types: [{ description: 'Export d’un gestionnaire de mots de passe', accept: { 'application/octet-stream': extensions } }],
    });
  } catch (cause) {
    if ((cause as { name?: unknown } | null)?.name === 'AbortError') return null;
    throw cause;
  }
  const handle = handles[0];
  if (handle === undefined) return null;
  return { file: await handle.getFile(), handle };
}

/**
 * Poignee d'un fichier depose, la ou le navigateur la donne. A appeler PENDANT l'evenement `drop` : la liste des
 * elements deposes n'est plus lisible apres lui.
 */
export function droppedImportFileHandle(transfer: DataTransfer | null | undefined): Promise<ImportFileHandle | null> {
  const item = transfer?.items?.[0] as (DataTransferItem & { getAsFileSystemHandle?: () => Promise<{ kind: string } | null> }) | undefined;
  if (item === undefined || item.kind !== 'file' || typeof item.getAsFileSystemHandle !== 'function') return Promise.resolve(null);
  return item
    .getAsFileSystemHandle()
    .then((handle) => (handle !== null && handle.kind === 'file' ? (handle as unknown as ImportFileHandle) : null))
    .catch(() => null);
}
